import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { LocationDescriptor as To } from "history";
import React from "react";

import { planChoosePlanUrl } from "@/mk2/apps/plan/urls";
import { Link } from "@/mk2/components/Link";
import { ListItem, OwnProps as ListItemOwnProps } from "@/mk2/components/ListItem";
import { PlusProBadge } from "@/mk2/components/PlusProBadge";
import { ProBadge } from "@/mk2/components/ProBadge";
import { url } from "@/mk2/helpers/urls";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { HistoryLocationState } from "@/mk2/services/browserHistory";
import { BZ2_GET_PLAN } from "@/mk/autogenerated/translation";
import { getLabel } from "@/mk/bazaar/product/categories";

import styles from "./CategoryListItem.mscss";

export interface Category {
    name: string;
    children?: Category[];
    disabled?: boolean;
    needsPlusPro?: boolean;
    needsPro?: boolean;
}

interface OwnProps {
    className?: string;
    style?: React.CSSProperties;
    label?: React.ReactNode;
    category: Category;
    disabled?: boolean;
    selected?: boolean;
    showIcon?: boolean;
    needsPlusPro?: boolean;
    needsPro?: boolean;
    isLast?: ListItemOwnProps["isLast"];
    count?: number;
    hasMoreIcon?: ListItemOwnProps["hasMoreIcon"];

    generateCategoryTo?(category: string): [To, Partial<HistoryLocationState>];
    onClick?(category: Category);
    onMouseEnter?(category: Category);
}

type Props = OwnProps;

const CategoryListItem: React.FunctionComponent<Props> = ({
    className,
    style,
    label,
    category,
    disabled,
    selected,
    showIcon = true,
    needsPlusPro = false,
    needsPro = false,
    count,
    isLast,
    generateCategoryTo,
    onClick,
    onMouseEnter,
    hasMoreIcon,
}) => {
    const onClickCallback = useEventCallback(() => {
        if (onClick) {
            onClick(category);
        }
    }, [onClick, category]);

    const onMouseEnterCallback = useEventCallback(() => {
        if (onMouseEnter) {
            onMouseEnter(category);
        }
    }, [onMouseEnter, category]);

    const catTo = generateCategoryTo ? generateCategoryTo(category.name) : undefined;

    return (
        <ListItem
            style={style}
            className={cx(styles.CategoryListItem, className)}
            hasMore={Boolean(category.children && category.children.length > 0)}
            hasMoreIcon={hasMoreIcon}
            isLast={isLast}
            icon={
                showIcon ? (
                    <div
                        className={cx(
                            styles.CategoryListItem__icon,
                            styles[`CategoryListItem__icon--${category.name}`],
                        )}
                    />
                ) : null
            }
            onClick={onClickCallback}
            onMouseEnter={onMouseEnterCallback}
            data-cy={`category-${category.name}`}
            to={catTo ? catTo[0] : undefined}
            toState={catTo ? catTo[1] : undefined}
        >
            <div
                className={cx(
                    styles.CategoryListItem__category,
                    selected && styles["CategoryListItem__category--selected"],
                )}
            >
                <span
                    className={cx(
                        styles.CategoryListItem__category__label,
                        disabled && styles["CategoryListItem__category__label--disabled"],
                    )}
                >
                    {label || getLabel(category.name)}
                    {(count || count === 0) && (
                        <span className={styles.CategoryListItem__count}> ({count})</span>
                    )}
                </span>

                {(needsPlusPro || needsPro) && (
                    <span className={styles.CategoryListItem__category__ad}>
                        <Link
                            to={url(planChoosePlanUrl, {}, { src: "editProduct" })}
                            target="_blank"
                        >
                            {BZ2_GET_PLAN}&nbsp;
                            {needsPro ? <ProBadge /> : <PlusProBadge />}
                        </Link>
                    </span>
                )}

                {selected && !needsPlusPro && !needsPro && (
                    <FontAwesomeIcon
                        className={styles.CategoryListItem__category__check}
                        icon={faCheck}
                    />
                )}
            </div>
        </ListItem>
    );
};

CategoryListItem.displayName = "CategoryListItem";

export default React.memo<Props>(CategoryListItem);
